import { useEffect, useRef, useState } from 'react';
import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Spinner } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import * as ComponentFunctions from './SettlementNameForm.Functions';
import { NAME_MAX_LENGTH, validateName } from './SettlementNameForm.Logic';
import '../../CashFlow/CashFlow.Form.Styles.css';
import './SettlementNameForm.Styles.css';

/** Drawer para cambiar el nombre de una liquidación. Un solo campo, sin vueltas. */
const SettlementNameFormComponent = (props: ComponentFunctions.Props) => {
    const [name, setName] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Al abrir: arranca con el nombre actual y el foco en el campo.
    useEffect(() => {
        if (!props.isOpen) return;
        setName(props.Settlement?.name ?? '');
        setSubmitted(false);
        setTouched(false);
        const timer = window.setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 50);
        return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.Settlement?.id]);

    // Guardado: se cierra cuando el store confirma el cambio pedido desde acá.
    useEffect(() => {
        if (submitted && !props.isUpdating && props.updatedOk) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitted, props.isUpdating, props.updatedOk]);

    useEffect(() => {
        if (props.failOnUpdating) setSubmitted(false);
    }, [props.failOnUpdating]);

    const error = validateName(name, props.Settlement?.name);
    const showError = (touched || submitted) && error;

    const save = () => {
        setSubmitted(true);
        if (error || !props.Settlement) return;
        props.RenameSettlement(props.Settlement.id, name.trim());
    };

    const close = () => { setSubmitted(false); props.onClose(); };

    return (
        <OverlayDrawer position="end" open={props.isOpen} size="small" onOpenChange={(_e, data) => { if (!data.open) close(); }}>
            <DrawerHeader>
                <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Cerrar" icon={<Dismiss24Regular />} onClick={close} />}>
                    <span className="cff-title">Cambiar nombre</span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <form className="cff-root" onSubmit={(event) => { event.preventDefault(); save(); }}>
                    {submitted && props.failOnUpdating && (
                        <div className="cff-banner" role="alert">
                            <strong>No se pudo guardar el nombre.</strong>
                            <span>{props.error?.ErrorMessage || 'Probá de nuevo.'}</span>
                        </div>
                    )}
                    <div className="cff-field">
                        <span className="cff-label">Nombre de la liquidación<span className="req" aria-hidden>*</span></span>
                        <input
                            ref={inputRef}
                            className={`snf-input${showError ? ' snf-input--error' : ''}`}
                            value={name}
                            maxLength={NAME_MAX_LENGTH + 20}
                            onChange={(event) => { setName(event.target.value); setTouched(true); }}
                            aria-invalid={!!showError}
                            aria-describedby="snf-help"
                        />
                        {showError
                            ? <span className="cff-error" role="alert">{error}</span>
                            : <span id="snf-help" className="cff-help">Como aparece en la lista, el PDF y las deudas asociadas. {name.trim().length}/{NAME_MAX_LENGTH}</span>}
                    </div>
                    {props.Settlement?.name && (
                        <div className="cff-help">Ahora se llama <strong>{props.Settlement.name}</strong>.</div>
                    )}
                </form>
            </DrawerBody>
            <DrawerFooter>
                <div className="cff-footer">
                    <button type="button" className="cff-secondary" onClick={close} disabled={props.isUpdating}>Cancelar</button>
                    <button type="button" className="cff-primary" onClick={save} disabled={props.isUpdating || (!!error && (touched || submitted))}>
                        {props.isUpdating && <Spinner size="extra-tiny" />} Guardar nombre
                    </button>
                </div>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default ComponentFunctions.connector(SettlementNameFormComponent);
